import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreatePerson, PersonType } from "@/hooks/usePeople";

const personTypes: { value: PersonType; label: string }[] = [
  { value: "contact", label: "Contact" },
  { value: "employee", label: "Employee" },
  { value: "freelancer", label: "Freelancer" },
];

const inlinePersonSchema = z.object({
  first_name: z
    .string()
    .min(1, "First name is required")
    .max(100, "First name is too long"),
  last_name: z
    .string()
    .min(1, "Last name is required")
    .max(100, "Last name is too long"),
  email: z
    .string()
    .email("Invalid email")
    .max(255)
    .optional()
    .or(z.literal("")),
  phone: z
    .string()
    .max(50, "Phone number is too long")
    .optional()
    .or(z.literal("")),
  person_type: z.enum(["contact", "employee", "freelancer"]),
});

type InlinePersonFormValues = z.infer<typeof inlinePersonSchema>;

interface InlinePersonFormProps {
  onSuccess: (newId: string) => void;
  onCancel: () => void;
  initialName?: string;
  defaultType?: PersonType;
  showTypeSelector?: boolean;
  companyId?: string | null;
}

export function InlinePersonForm({
  onSuccess,
  onCancel,
  initialName = "",
  defaultType = "contact",
  showTypeSelector = true,
  companyId,
}: InlinePersonFormProps) {
  const createPerson = useCreatePerson();

  // Parse initial name into first/last
  const nameParts = initialName.trim().split(" ");
  const defaultFirstName = nameParts[0] || "";
  const defaultLastName = nameParts.slice(1).join(" ") || "";

  const form = useForm<InlinePersonFormValues>({
    resolver: zodResolver(inlinePersonSchema),
    defaultValues: {
      first_name: defaultFirstName,
      last_name: defaultLastName,
      email: "",
      phone: "",
      person_type: defaultType,
    },
  });

  const handleSubmit = async (values: InlinePersonFormValues) => {
    try {
      const result = await createPerson.mutateAsync({
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email || null,
        phone: values.phone || null,
        person_type: values.person_type,
        company_id: companyId || null,
      });
      onSuccess(result.id);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          form.handleSubmit(handleSubmit)(e);
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name *</FormLabel>
                <FormControl>
                  <Input placeholder="First name" {...field} autoFocus />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+49 123 456789" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {showTypeSelector && (
          <FormField
            control={form.control}
            name="person_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Type *</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {personTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={createPerson.isPending}>
            {createPerson.isPending ? "Creating..." : "Create Person"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
